<template>
    <AppLayout>
        <router-view />
    </AppLayout>

    <RouteSpinner v-if="store.getters.isRouting || store.getters.isRefreshing" />
</template>

<script setup lang="ts">

import { useStore } from 'vuex'
import RouteSpinner from '@/components/misc/RouteSpinner.vue'
import { onMounted, watch, ref } from 'vue'
import eventBus from '@/utils/eventBus'
import { useEventSource } from '@vueuse/core'

const store = useStore()

const eventSource = (topics) => {

	if (!store.getters.eventSourceUrl) {
		throw 'No event source URL available'
	}
	
	const url = new URL(store.getters.eventSourceUrl)
	
	url.searchParams.append('authorization', store.getters.token)
	
	topics.forEach(topic => {
		url.searchParams.append('topic', topic)
	})

	return useEventSource(
	  url,
	  ['message'], // named events
	  {
		  autoReconnect: {
			retries: 3,
			delay: 1000,
			onFailed() {
			  console.log('Failed to connect EventSource after 3 retries')
			},
		  },
		  serializer: {
			read: rawData => JSON.parse(rawData),
		  },
	  }
	)

}

const eventSourceClose = ref(null)

watch(
	() => [store.getters.eventSourceUrl, store.getters.authedMemberInfo?.id, store.getters.workspaceUuid],
	(current, previous) => {
	
		if (store.getters.eventSourceUrl && store.getters.authedMemberInfo?.id && store.getters.workspaceUuid) {
		
			let lastEventType;
		
			const { event, status, data, close } = eventSource([
				'workspace-' + store.getters.workspaceUuid,
				'workspacemember-' + store.getters.authedMemberInfo.id
			])
			
			eventSourceClose.value = close
			
			// The 'event' ref holds the type of the last event received
			watch(event, (newEvent) => {
				lastEventType = newEvent
			});
			
			watch(data, receivedData => {
				// With named events, it seems that this watcher gets called once before lastEventType is updated, and once after.
				if (lastEventType) {
					console.log('Received data:', receivedData)
					eventBus.emit('sse.' + lastEventType, receivedData)
				}
			});
			
			// The 'status' ref indicates the connection state ('CONNECTING', 'OPEN', or 'CLOSED')
			watch(status, (newStatus) => {
				console.log('SSE connection:', newStatus);
			});
			
		} else if (eventSourceClose.value) {
			eventSourceClose.value()
			eventSourceClose.value = null
		}
	},
	{immediate: true}
)


// GLOBAL SEARCH INPUT HANDLER
onMounted(() => {
  // Listen for input events anywhere in the app
  document.addEventListener('input', (e) => {
    const el = e.target as HTMLInputElement
    if (el?.classList?.contains('search-field')) {
      el.classList.toggle('has-value', el.value.trim() !== '')
    }
  })

  // Handle already-filled fields on page load
  document.querySelectorAll('.search-field').forEach((el: any) => {
    el.classList.toggle('has-value', el.value.trim() !== '')
  })
})
</script>
